"use client";

import { forwardRef } from "react";
import { cn } from "~/lib/utils";

export interface CheckboxCardProps
  extends Omit<React.HTMLAttributes<HTMLButtonElement>, "onChange"> {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  title: string;
  subtitle: string;
  disabled?: boolean;
}

const CheckboxCard = forwardRef<HTMLButtonElement, CheckboxCardProps>(
  (
    { className, checked = false, onChange, title, subtitle, disabled = false, ...props },
    ref
  ) => {
    const handleClick = () => {
      if (!disabled && onChange) {
        onChange(!checked);
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={cn(
          "flex h-[68px] w-full items-center gap-2 rounded-md border border-[rgba(0,0,47,0.15)] bg-[rgba(255,255,255,0.9)] px-4 py-3.5 text-left transition-colors",
          checked && "border-[rgba(0,6,46,0.2)] bg-[rgba(255,255,255,1)]",
          !disabled && "hover:bg-[#fafafa] cursor-pointer",
          disabled && "cursor-not-allowed opacity-50",
          className
        )}
        onClick={handleClick}
        {...props}
      >
        <div className="flex flex-1 flex-col justify-center gap-0 leading-5 text-sm">
          <p className="font-semibold text-[rgba(0,5,9,0.89)]">{title}</p>
          <p className="font-normal text-[rgba(0,7,20,0.62)]">{subtitle}</p>
        </div>
        <div className="flex h-5 items-center">
          <div
            className={cn(
              "flex h-4 w-4 items-center justify-center rounded-[3px] border transition-colors",
              checked
                ? "border-[#1c3144] bg-[#1c3144]"
                : "border-[rgba(0,6,46,0.2)] bg-[rgba(255,255,255,0.9)]"
            )}
          >
            {checked && (
              <svg
                className="h-2.5 w-2.5 text-white"
                viewBox="0 0 10 8"
                fill="none"
              >
                <path
                  d="M1 4L3.5 6.5L9 1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </div>
      </button>
    );
  }
);

CheckboxCard.displayName = "CheckboxCard";

export { CheckboxCard };

